import { useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import _ from "lodash";
import { useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from "use-query-params";
import * as yup from "yup";

import {
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import Table, { TableControls } from "src/components/Table";
import FormikTextField from "src/components/form/FormikTextField";
import {
  listTenants,
  listTenants_listTenants
} from "src/graphql/generated/listTenants";
import {
  tenantCreate,
  tenantCreateVariables
} from "src/graphql/generated/tenantCreate";
import { LIST_TENANTS, CREATE_TENANT } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";

const tenantSchema = yup.object().shape({
  name: yup.string().required("Required"),
  slug: yup
    .string()
    .matches(
      /^[a-z0-9-]+$/,
      "Slug must contain only lowercase letters, digits and dashes"
    )
    .required("Required"),
  ownerFullName: yup.string(),
  ownerEmail: yup.string().email()
});

function AddTenantDialog({
  open,
  onClose,
  handleCreateTenant
}: {
  open: boolean;
  onClose: () => void;
  handleCreateTenant: (values: {
    name: string;
    slug: string;
    ownerFullName: string | undefined;
    ownerEmail: string | undefined;
  }) => Promise<void>;
}): JSX.Element {
  const onSubmit = async (params: {
    name: string;
    slug: string;
    ownerFullName: string | undefined;
    ownerEmail: string | undefined;
  }) => {
    await handleCreateTenant(params);
    onClose();
  };

  return (
    <>
      <Dialog open={open} maxWidth="md">
        <DialogTitle>Add Tenant</DialogTitle>
        <Formik
          initialValues={{
            name: "",
            slug: "",
            ownerFullName: "",
            ownerEmail: ""
          }}
          onSubmit={onSubmit}
          validationSchema={tenantSchema}
        >
          {({ isSubmitting }) => (
            <Form>
              <DialogContent>
                <FormikTextField
                  fullWidth
                  label="Name"
                  name="name"
                  type="text"
                />
                <FormikTextField
                  fullWidth
                  label="Slug"
                  name="slug"
                  type="text"
                />
                <FormikTextField
                  fullWidth
                  label="Owner Full Name (optional)"
                  name="ownerFullName"
                  type="text"
                />
                <FormikTextField
                  fullWidth
                  label="Owner Email (optional)"
                  name="ownerEmail"
                  type="text"
                />
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    onClose();
                  }}
                  color="primary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  color="primary"
                  variant="contained"
                >
                  {isSubmitting ? "Creating..." : "Create Tenant"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

const TENANT_SORTS = [
  {
    key: "name_asc",
    label: "Name, A-Z"
  },
  {
    key: "name_desc",
    label: "Name, Z-A"
  },
  {
    key: "slug_asc",
    label: "Slug, A-Z"
  },
  {
    key: "slug_desc",
    label: "Slug, Z-A"
  }
] as const;

const validateSort = (sort: string): TenantSort => {
  return TENANT_SORTS.find(s => s.key === sort)?.key ?? TENANT_SORTS[0].key;
};

type TenantSort = (typeof TENANT_SORTS)[number]["key"];

type TenantRow = { id: string } & listTenants_listTenants;

function TenantTable({
  rows,
  handleAddTenant
}: {
  rows: TenantRow[];
  handleAddTenant: (params: {
    name: string;
    slug: string;
    ownerFullName: string | undefined;
    ownerEmail: string | undefined;
  }) => Promise<void>;
}): JSX.Element {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, TENANT_SORTS[0].key)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [addTenantDialogOpen, toggleAddTenantDialog] = useState(false);

  const filteredTenants = search
    ? rows.filter(
        t =>
          t.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
          t.slug.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      )
    : rows;

  const validatedSort: TenantSort = validateSort(sort);

  const sortedTenants = filteredTenants.sort((a, b) => {
    switch (validatedSort) {
      case "slug_asc":
        return (a.slug || "").localeCompare(b.slug || "");
      case "slug_desc":
        return (b.slug || "").localeCompare(a.slug || "");
      case "name_asc":
        return (a.name || "").localeCompare(b.name || "");
      case "name_desc":
        return (b.name || "").localeCompare(a.name || "");
    }
  });

  return (
    <>
      <TableControls
        addButtons={[
          {
            label: "Add Tenant",
            onClick: () => toggleAddTenantDialog(true)
          }
        ]}
        sort={{
          value: sort,
          onChange: value => {
            setSort(value as TenantSort);
            setPage(0);
          },
          options: TENANT_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label
          }))
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search for a tenant..."
        }}
      />
      <Table
        columns={[
          {
            key: "slug",
            title: "Slug",
            renderValue: row => row.slug
          },
          {
            key: "name",
            title: "Name",
            renderValue: row => row.name
          },
          {
            key: "deactivatedAt",
            title: "Deactivated at",
            renderValue: row => row.deactivatedAt
          }
        ]}
        rows={sortedTenants}
        rowActions={[]}
        emptyState="No results"
        totalCount={rows.length}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newRowsPerPage => {
          setPageSize(newRowsPerPage);
          setPage(0);
        }}
        page={page}
        onChangePage={setPage}
      />
      <AddTenantDialog
        open={addTenantDialogOpen}
        onClose={() => {
          toggleAddTenantDialog(false);
        }}
        handleCreateTenant={handleAddTenant}
      />
    </>
  );
}

export function StaffTenantAdmin(): JSX.Element {
  const { loading, error, data } = useQuery<listTenants>(LIST_TENANTS);

  const [createTenant] = useMutation<tenantCreate, tenantCreateVariables>(
    CREATE_TENANT,
    {
      onErrorSnackbarMessage: "Failed to create tenant",
      refetchQueries: ["listTenants"],
      awaitRefetchQueries: true
    }
  );

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <Container>
      <PageTitle title="Tenants" />
      <TenantTable
        rows={data!.listTenants!.map(tenantFields => ({
          id: tenantFields!.slug,
          ...tenantFields!
        }))}
        handleAddTenant={async (params: {
          name: string;
          slug: string;
          ownerFullName: string | undefined;
          ownerEmail: string | undefined;
        }) => {
          await createTenant({ variables: params });
        }}
      />
    </Container>
  );
}
